/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { FormHelperText, Switch, FormControlLabel } from "@mui/material";
import PropTypes from "prop-types";
import { Grid, Card } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchData, saveData, getLanguages } from "./Data";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

function EditTestimonio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [idiomas, setIdiomas] = useState([]);
  const [idiomaSeleccionado, setIdiomaSeleccionado] = useState(null);

  /* Estado inicial — enableReinitialize lo sincronizará con Formik */
  const [data, setData] = useState({
    ID: 0,
    ACTIVO: true,
    NOMBRE: "",
    TEXTO: "",
    POS: 1,
    LANGUAGE_ID: 1,
  });

  /* Cargar idiomas al montar */
  useEffect(() => {
    const load = async () => {
      const list = await getLanguages();
      setIdiomas(list || []);
      const defecto = (list || []).find((l) => Number(l.ID) === 1);
      if (defecto && !idiomaSeleccionado) setIdiomaSeleccionado(defecto);
    };
    load();
  }, []);

  /* Carga los datos cuando es edición (id > 0) y hay idioma seleccionado */
  useEffect(() => {
    const loadData = async () => {
      if (!idiomaSeleccionado) return;
      const langId = Number(idiomaSeleccionado.ID);
      const res = await fetchData(id, langId);
      if (res) {
        setData({
          ID: res.ID ?? 0,
          ACTIVO: Boolean(res.ACTIVO),
          NOMBRE: res.NOMBRE || "",
          TEXTO: res.TEXTO || "",
          POS: res.POS ?? 1,
          LANGUAGE_ID: langId,
        });
      }
    };
    if (Number(id) > 0 && idiomaSeleccionado) loadData();
    else if (Number(id) === 0 && idiomaSeleccionado) {
      setData((prev) => ({
        ...prev,
        LANGUAGE_ID: Number(idiomaSeleccionado.ID),
      }));
    }
  }, [id, idiomaSeleccionado]);

  /* ── Toast helpers ── */
  const CustomToastContent = ({ title, subtitle }) => (
    <div>
      <strong style={{ fontSize: "16px" }}>{title}</strong>
      {subtitle && <p style={{ fontSize: "14px" }}>{subtitle}</p>}
    </div>
  );
  CustomToastContent.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
  };
  CustomToastContent.defaultProps = {
    title: "¡Guardado correctamente!",
    subtitle: "",
  };

  const msgOk = (title = "¡Guardado correctamente!", subtitle = "") =>
    toast.success(<CustomToastContent title={title} subtitle={subtitle} />, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "dark",
      transition: Bounce,
    });

  const msgError = (title = "Error al guardar", subtitle = "") =>
    toast.error(<CustomToastContent title={title} subtitle={subtitle} />, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "dark",
      transition: Bounce,
    });

  /* ── Validación Yup ── */
  const validationSchema = Yup.object().shape({
    NOMBRE: Yup.string().max(500).required("Nombre es obligatorio"),
    TEXTO: Yup.string().required("El testimonio es obligatorio"),
    POS: Yup.number().required("Posición es obligatoria").positive().integer(),
    LANGUAGE_ID: Yup.number().optional(),
  });

  /* ── Submit — envía LANGUAGE_ID para TESTIMONIOS_LANG ── */
  const handleSubmit = async (values, actions) => {
    try {
      const payload = {
        ...values,
        POS: Number(values.POS),
        LANGUAGE_ID: Number(values.LANGUAGE_ID || idiomaSeleccionado?.ID || 1),
      };
      const response = await saveData(payload);
      if (response.ok) {
        const msgResponse = await response.json();
        if (msgResponse.error && msgResponse.error !== "") {
          msgError(msgResponse.error);
        } else {
          msgOk();
          const newId = msgResponse?.response?.[0]?.ID ?? id;
          navigate("/admin/testimonios/" + newId);
        }
      } else {
        const errorData = await response.json();
        msgError(`Error al guardar: ${errorData.message}`);
      }
    } catch (error) {
      msgError("Ocurrió un error al guardar los datos");
      console.error(error);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const titleNav = id == 0 ? "nuevo testimonio" : `testimonio ${data.NOMBRE}`;

  return (
    <DashboardLayout>
      <ToastContainer />
      <DashboardNavbar title={`Edición de ${titleNav}`} />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Edición del registro
                </MDTypography>
              </MDBox>
              <MDBox py={3} px={3}>
                <Formik
                  initialValues={data}
                  enableReinitialize
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    isSubmitting,
                    touched,
                    values,
                    setFieldValue,
                  }) => (
                    <Form>
                      <MDBox pt={0} pb={3}>
                        <Grid container spacing={4}>
                          {/* Selector de idioma (traducciones TESTIMONIOS_LANG) */}
                          <Grid item xs={12}>
                            <Autocomplete
                              options={idiomas}
                              getOptionLabel={(opt) => opt?.ACRONIMO_ISO?.toUpperCase() || ""}
                              value={idiomaSeleccionado}
                              onChange={(_, newVal) => {
                                setIdiomaSeleccionado(newVal);
                                if (newVal) setFieldValue("LANGUAGE_ID", Number(newVal.ID));
                              }}
                              renderInput={(params) => (
                                <TextField {...params} label="Idioma" variant="standard" />
                              )}
                            />
                          </Grid>
                          {/* Posición */}
                          <Grid item xs={2}>
                            <MDInput
                              name="POS"
                              fullWidth
                              label="Posición"
                              type="number"
                              variant="standard"
                              value={values.POS}
                              error={touched.POS && Boolean(errors.POS)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.POS && errors.POS && (
                              <FormHelperText error>{errors.POS}</FormHelperText>
                            )}
                          </Grid>

                          {/* Nombre */}
                          <Grid item xs={8}>
                            <MDInput
                              name="NOMBRE"
                              fullWidth
                              label="Nombre del cliente"
                              variant="standard"
                              value={values.NOMBRE}
                              error={touched.NOMBRE && Boolean(errors.NOMBRE)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.NOMBRE && errors.NOMBRE && (
                              <FormHelperText error>{errors.NOMBRE}</FormHelperText>
                            )}
                          </Grid>

                          {/* Activo */}
                          <Grid item xs={2} textAlign="start">
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={values.ACTIVO}
                                  onChange={handleChange}
                                  name="ACTIVO"
                                />
                              }
                              label="Activo"
                            />
                          </Grid>

                          {/* Texto del testimonio */}
                          <Grid item xs={12}>
                            <MDInput
                              type="text"
                              variant="standard"
                              fullWidth
                              name="TEXTO"
                              label="Testimonio"
                              error={touched.TEXTO && Boolean(errors.TEXTO)}
                              value={values.TEXTO}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              multiline
                              rows={6}
                            />
                            {touched.TEXTO && errors.TEXTO && (
                              <FormHelperText error>{errors.TEXTO}</FormHelperText>
                            )}
                          </Grid>

                          {/* Botón guardar */}
                          <Grid item xs={12}>
                            <MDBox display="flex" justifyContent="flex-end">
                              <MDButton
                                type="submit"
                                variant="gradient"
                                color="primary"
                                size="medium"
                                disabled={isSubmitting}
                              >
                                GUARDAR DATOS
                              </MDButton>
                            </MDBox>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </Form>
                  )}
                </Formik>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditTestimonio;
